<template>
  <div class="tickets-board">
    <div class="board-header">
      <h1 class="board-title">Тикеты</h1>
    </div>

    <div v-loading="loading" class="board-columns">
      <div
        v-for="col in columns"
        :key="col.status"
        class="board-column"
        @dragover.prevent="onDragOver(col.status)"
        @dragleave="onDragLeave"
        @drop.prevent="onDrop(col.status)"
        :class="{ 'column-drag-over': dragOverStatus === col.status }"
      >
        <div class="column-header">
          <div class="column-header-left">
            <span class="column-dot" :style="{ background: col.color }"></span>
            <span class="column-label">{{ col.label }}</span>
            <span class="column-count">{{ ticketsByStatus(col.status).length }}</span>
          </div>
        </div>

        <div class="column-body">
          <div
            v-for="ticket in ticketsByStatus(col.status)"
            :key="ticket.id"
            class="ticket-card"
            draggable="true"
            @dragstart="onDragStart(ticket)"
            @dragend="onDragEnd"
            @click="openTicket(ticket)"
          >
            <div class="ticket-card-title">{{ ticket.title }}</div>
            <div class="ticket-card-meta">
              <span
                v-if="ticket.order_code"
                class="ticket-order"
                @click.stop="goToAccounting(ticket.order_code)"
              >#{{ ticket.order_code }}</span>
              <span class="ticket-date">{{ formatDate(ticket.created_at) }}</span>
            </div>
          </div>

          <div v-if="ticketsByStatus(col.status).length === 0" class="column-empty">
            <span v-if="dragOverStatus === col.status">Отпустите здесь</span>
            <span v-else>Нет тикетов</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Ticket detail modal -->
    <el-dialog
      v-if="selectedTicket"
      :visible.sync="dialogVisible"
      width="580px"
      custom-class="ticket-dialog"
      @close="closeTicket"
    >
      <div slot="title" class="ticket-dialog-header">
        <div class="ticket-dialog-title">{{ selectedTicket.title }}</div>
        <div class="ticket-dialog-meta">
          <el-tag size="mini" :type="statusType(selectedTicket.status)" effect="plain">
            {{ statusLabel(selectedTicket.status) }}
          </el-tag>
          <span
            v-if="selectedTicket.order_code"
            class="dialog-order"
            @click="goToAccounting(selectedTicket.order_code)"
          >#{{ selectedTicket.order_code }}</span>
          <span class="dialog-date">{{ formatDate(selectedTicket.created_at) }}</span>
        </div>
      </div>

      <div v-loading="detailLoading" class="ticket-dialog-body">
        <div v-if="selectedTicket.description" class="ticket-description">
          {{ selectedTicket.description }}
        </div>

        <div class="ticket-actions">
          <el-button
            v-if="selectedTicket.status !== 'new'"
            size="small"
            plain
            @click="moveTicketInModal(prevStatus(selectedTicket.status))"
          >
            <i class="el-icon-arrow-left"></i> {{ statusLabel(prevStatus(selectedTicket.status)) }}
          </el-button>
          <el-button
            v-if="selectedTicket.status !== 'done'"
            size="small"
            type="primary"
            @click="moveTicketInModal(nextStatus(selectedTicket.status))"
          >
            {{ statusLabel(nextStatus(selectedTicket.status)) }} <i class="el-icon-arrow-right"></i>
          </el-button>
        </div>

        <div class="ticket-timeline">
          <div class="timeline-title">История</div>
          <div v-if="comments.length === 0" class="timeline-empty">Комментариев пока нет</div>
          <div v-for="comment in comments" :key="comment.id" class="timeline-item">
            <div class="timeline-avatar">{{ (comment.author || 'М')[0].toUpperCase() }}</div>
            <div class="timeline-content">
              <div class="timeline-meta">
                <span class="timeline-author">{{ comment.author }}</span>
                <span class="timeline-date">{{ formatDate(comment.created_at) }}</span>
              </div>
              <div class="timeline-text">{{ comment.text }}</div>
            </div>
          </div>
        </div>

        <div class="comment-form">
          <div class="comment-avatar">М</div>
          <div class="comment-input-wrap">
            <el-input
              v-model="newComment"
              type="textarea"
              :rows="2"
              placeholder="Написать комментарий..."
              resize="none"
            ></el-input>
            <div class="comment-submit">
              <el-button
                type="primary"
                size="small"
                :loading="commentSaving"
                :disabled="!newComment.trim()"
                @click="submitComment"
              >Отправить</el-button>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getTickets, getTicket, updateTicketStatus, addTicketComment } from "@/api/tickets";
import { getAccountings } from "@/api/accounting";
import { format } from "date-fns";

const STATUS_ORDER = ["new", "in_process", "done"];

export default {
  name: "TicketsKanban",
  data: () => ({
    loading: false,
    detailLoading: false,
    commentSaving: false,
    tickets: [],
    selectedTicket: null,
    dialogVisible: false,
    comments: [],
    newComment: "",
    draggingTicket: null,
    dragOverStatus: null,
    columns: [
      { status: "new", label: "Новые", color: "#94a3b8" },
      { status: "in_process", label: "В работе", color: "#f59e0b" },
      { status: "done", label: "Готово", color: "#22c55e" },
    ],
  }),
  async created() {
    await this.loadTickets();
  },
  methods: {
    async loadTickets() {
      this.loading = true;
      this.tickets = await getTickets();
      this.loading = false;
    },
    ticketsByStatus(status) {
      return this.tickets.filter((t) => t.status === status);
    },
    nextStatus(current) {
      const idx = STATUS_ORDER.indexOf(current);
      return STATUS_ORDER[idx + 1] || current;
    },
    prevStatus(current) {
      const idx = STATUS_ORDER.indexOf(current);
      return STATUS_ORDER[idx - 1] || current;
    },
    async moveTicket(ticket, newStatus) {
      if (ticket.status === newStatus) return;
      const oldStatus = ticket.status;
      ticket.status = newStatus; // optimistic update
      try {
        const res = await updateTicketStatus(ticket.id, newStatus);
        if (!res.success) {
          ticket.status = oldStatus;
          this.$message.error(res.message || "Ошибка");
        }
      } catch (e) {
        ticket.status = oldStatus;
        this.$message.error(e.message);
      }
    },
    async moveTicketInModal(newStatus) {
      await this.moveTicket(this.selectedTicket, newStatus);
    },

    // Drag and drop
    onDragStart(ticket) {
      this.draggingTicket = ticket;
    },
    onDragEnd() {
      this.draggingTicket = null;
      this.dragOverStatus = null;
    },
    onDragOver(status) {
      if (this.draggingTicket && this.draggingTicket.status !== status) {
        this.dragOverStatus = status;
      }
    },
    onDragLeave() {
      this.dragOverStatus = null;
    },
    async onDrop(status) {
      this.dragOverStatus = null;
      if (this.draggingTicket) {
        await this.moveTicket(this.draggingTicket, status);
      }
    },

    async openTicket(ticket) {
      this.selectedTicket = ticket;
      this.dialogVisible = true;
      this.comments = [];
      this.newComment = "";
      this.detailLoading = true;
      try {
        const res = await getTicket(ticket.id);
        this.comments = res.comments;
      } catch (e) {
        this.$message.error(e.message);
      }
      this.detailLoading = false;
    },
    closeTicket() {
      this.selectedTicket = null;
      this.comments = [];
      this.newComment = "";
    },

    async goToAccounting(orderCode) {
      try {
        const data = await getAccountings({ basis: orderCode });
        const list = data.data || [];
        const item = list.find((a) => String(a.basis) === String(orderCode));
        if (item) {
          this.$router.push({ name: "accounting-item", params: { id: item.id } });
        } else {
          this.$message.warning("Документ не найден");
        }
      } catch (e) {
        this.$message.error(e.message);
      }
    },

    async submitComment() {
      if (!this.newComment.trim()) return;
      this.commentSaving = true;
      try {
        const res = await addTicketComment(this.selectedTicket.id, this.newComment);
        if (res.success) {
          this.comments.push(res.comment);
          this.newComment = "";
        } else {
          this.$message.error(res.message || "Ошибка");
        }
      } catch (e) {
        this.$message.error(e.message);
      }
      this.commentSaving = false;
    },

    formatDate(val) {
      if (!val) return "";
      const ms = val > 1e10 ? val : val * 1000;
      return format(new Date(ms), "dd.MM.yyyy HH:mm");
    },
    statusLabel(status) {
      return { new: "Новый", in_process: "В работе", done: "Готово" }[status] || status;
    },
    statusType(status) {
      return { new: "info", in_process: "warning", done: "success" }[status] || "";
    },
  },
};
</script>

<style scoped>
.tickets-board {
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  min-height: 100vh;
}

.board-header {
  margin-bottom: 20px;
}

.board-title {
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.board-columns {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex: 1;
}

.board-column {
  flex: 1;
  min-width: 0;
  background: #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: border-color 0.15s;
}

.board-column.column-drag-over {
  border-color: #3b82f6;
  background: #eff6ff;
}

.column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #e2e8f0;
}

.column-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.column-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.column-label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.column-count {
  font-size: 12px;
  color: #94a3b8;
  background: #e2e8f0;
  border-radius: 10px;
  padding: 1px 7px;
  font-weight: 500;
}

.column-body {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 80px;
}

.ticket-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 14px;
  cursor: grab;
  transition: box-shadow 0.15s, border-color 0.15s;
  user-select: none;
}

.ticket-card:active {
  cursor: grabbing;
}

.ticket-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border-color: #cbd5e1;
}

.ticket-card-title {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
  margin-bottom: 8px;
  line-height: 1.4;
}

.ticket-card-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ticket-order {
  font-size: 11px;
  color: #3b82f6;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
}

.ticket-order:hover {
  color: #2563eb;
}

.ticket-date {
  font-size: 11px;
  color: #94a3b8;
}

.column-empty {
  text-align: center;
  color: #cbd5e1;
  font-size: 13px;
  padding: 24px 0;
}

/* Dialog */
.ticket-dialog-header {
  padding-bottom: 4px;
}

.ticket-dialog-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 8px;
}

.ticket-dialog-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.dialog-order {
  font-size: 12px;
  color: #3b82f6;
  cursor: pointer;
  text-decoration: underline;
}

.dialog-order:hover {
  color: #2563eb;
}

.dialog-date {
  font-size: 12px;
  color: #94a3b8;
}

.ticket-dialog-body {
  padding-top: 4px;
}

.ticket-description {
  font-size: 13px;
  color: #475569;
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  white-space: pre-line;
  line-height: 1.6;
}

.ticket-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.ticket-timeline {
  margin-bottom: 20px;
}

.timeline-title {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 14px;
}

.timeline-empty {
  font-size: 13px;
  color: #cbd5e1;
  text-align: center;
  padding: 16px 0;
}

.timeline-item {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.timeline-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.timeline-content {
  flex: 1;
  min-width: 0;
}

.timeline-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.timeline-author {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.timeline-date {
  font-size: 11px;
  color: #94a3b8;
}

.timeline-text {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
  white-space: pre-line;
}

.comment-form {
  display: flex;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}

.comment-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #3b82f6;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.comment-input-wrap {
  flex: 1;
  min-width: 0;
}

.comment-submit {
  margin-top: 8px;
  text-align: right;
}
</style>
